import { useCandidateAuthStore } from "@repo/auth";
import { Card, Progress } from "@repo/ui";
import { useAction, useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  StatutGlobalEnum,
  estEvaluationSoumise,
} from "@convex/schema";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import { useCheatingDetection } from "../../hooks/useCheatingDetection";
import { useTTS } from "../../hooks/useTTS";
import type { EntStructEvaluation, MessageRole } from "../../lib/types";
import { isBackendError } from "../../lib/types";
import { calculerProgressionQuestions } from "../../lib/utils";
import { useChatStore } from "../../stores/chatStore";
import { ChatInputArea } from "./ChatInputArea";
import { ChatMessages } from "./ChatMessages";
import { CompletionModal } from "./CompletionModal";
import { IntroModal } from "./IntroModal";

interface Message {
  role: MessageRole;
  content: string;
}

interface ChatInterfaceProps {
  evaluation: EntStructEvaluation;
  token: string;
}

export function ChatInterface({ evaluation, token }: ChatInterfaceProps) {
  // parcoursId depuis le store d'auth (renseigné quand on arrive du portail candidat)
  const parcoursIdFromStore = useCandidateAuthStore(
    (state) => state.parcoursId,
  );

  // À défaut de store, on retrouve parcoursId depuis evaluationId
  // On ne saute la query que si parcoursIdFromStore est vraiment renseigné (chaîne non vide)
  const parcoursFromEval = useQuery(
    api.parcoursEvaluations.obtenirParcoursIdDepuisEvaluation,
    parcoursIdFromStore ? "skip" : { evaluationId: evaluation._id },
  );

  // parcoursId : le store en priorité, la query en repli
  // À noter : parcoursFromEval vaut undefined pendant le chargement, null si introuvable
  const parcoursId = parcoursIdFromStore || parcoursFromEval?.parcoursId;

  const {
    isTyping,
    setIsTyping,
    currentInput,
    setCurrentInput,
    addToCurrentInput,
    isSubmitting,
    setIsSubmitting,
    showCompletionModal,
    setShowCompletionModal,
    isRecording,
    setIsRecording,
    isTranscribing,
    setIsTranscribing,
    transcribedLength,
    addTranscribedLength,
    resetTranscribedLength,
  } = useChatStore();

  const {
    onQuestionAffichee,
    onKeystroke,
    onPaste,
    getCheatingMeta,
    reset: resetCheatingMeta,
  } = useCheatingDetection();

  const [isInterviewFinished, setIsInterviewFinished] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioVolume, setAudioVolume] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null,
  );

  // État de la modale d'introduction — affichée seulement si l'évaluation n'a pas commencé
  const [showIntroModal, setShowIntroModal] = useState(
    evaluation.statut === StatutGlobalEnum.NON_COMMENCE,
  );
  const [isStartingInterview, setIsStartingInterview] = useState(false);

  const { audioStates, handleVocalize } = useTTS();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef<number>(0);
  const userScrolledUpRef = useRef<boolean>(false);
  const questionDisplayedAtRef = useRef<number | null>(null);
  const typewriterEndAtRef = useRef<number | null>(null);
  const firstResponseAtRef = useRef<number | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [isMobile] = useState(
    () => window.matchMedia("(hover: none) and (pointer: coarse)").matches,
  );

  // Mode lecture seule ?
  const isReadOnly = estEvaluationSoumise(evaluation.statut);

  // Nombre total de questions de la scorecard (version figée, ou slug pour
  // une évaluation antérieure au backfill)
  const totalQuestionsTemplate = useQuery(
    api.entStruct.getNombreQuestionsScorecard,
    evaluation.donnees.scorecardVersionId || evaluation.donnees.template
      ? {
          scorecardVersionId: evaluation.donnees.scorecardVersionId,
          templateSlug: evaluation.donnees.template,
        }
      : "skip",
  );

  // Récupérer l'URL du portail candidat
  const candidatePortailUrl = useQuery(
    api.entStruct.getCandidatePortailUrl,
    {},
  );

  // Calcul de la progression
  const progression = useMemo(
    () =>
      calculerProgressionQuestions(
        evaluation.donnees.questionsEtat,
        evaluation.donnees.questionCouranteId,
        totalQuestionsTemplate ?? undefined,
      ),
    [
      evaluation.donnees.questionsEtat,
      evaluation.donnees.questionCouranteId,
      totalQuestionsTemplate,
    ],
  );

  const envoyerMessage = useAction(api.entStruct.envoyerMessageCandidat);
  const initialiserEntretien = useAction(api.entStruct.initialiserEntretien);
  const terminerEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const demarrerEvaluation = useMutation(api.evaluations.demarrerEvaluation);
  const transcrireAudio = useAction(api.transcription.transcrireAudio);

  // Durée max
  const maxDuration = Number(
    process.env.BUN_PUBLIC_MAX_RECORDING_DURATION_MINUTES || 5,
  );

  // Timeout silence
  const silenceTimeout = Number(
    process.env.BUN_PUBLIC_SILENCE_TIMEOUT_SECONDS || 30,
  );

  const isTtsEnabled = process.env.BUN_PUBLIC_TTS_ENABLED === "true";

  // Extrait les messages de evaluation.donnees.reponses
  const messages: Message[] = Object.entries(evaluation.donnees.reponses)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([_, msg]) => ({
      role: msg.role,
      content: msg.content.replace(" ENT_FINISHED", "").trim(),
    }));

  // L'entretien est-il terminé ?
  const lastMessageEntry = Object.entries(evaluation.donnees.reponses)
    .sort(([a], [b]) => Number(a) - Number(b))
    .pop();
  const lastMessage = lastMessageEntry ? lastMessageEntry[1] : null;
  const isFinished =
    lastMessage && typeof lastMessage.content === "string"
      ? lastMessage.content.includes("ENT_FINISHED")
      : false;

  // L'utilisateur est-il près du bas ? (seuil de 100 px)
  const checkIfNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  // Suit la position de l'utilisateur au fil du défilement
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollTop } = scrollContainerRef.current;
    const nearBottom = checkIfNearBottom();

    // Détecte un défilement manuel vers le haut
    const scrolledUp = scrollTop < lastScrollTopRef.current - 10;
    lastScrollTopRef.current = scrollTop;

    if (scrolledUp && !nearBottom) {
      userScrolledUpRef.current = true;
    } else if (nearBottom) {
      userScrolledUpRef.current = false;
    }

    setIsNearBottom(nearBottom);
  }, [checkIfNearBottom]);

  // Défilement automatique pendant que la machine à écrire fait grandir la bulle
  // (MutationObserver sur les changements du DOM)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new MutationObserver(() => {
      if (!userScrolledUpRef.current) {
        const isMobile = window.matchMedia(
          "(hover: none) and (pointer: coarse)",
        ).matches;
        messagesEndRef.current?.scrollIntoView({
          behavior: isMobile ? "instant" : "smooth",
        });
      }
    });
    observer.observe(container, {
      subtree: true,
      characterData: true,
      childList: true,
    });
    return () => observer.disconnect();
  }, []);

  // Défilement automatique vers le bas à l'arrivée de nouveaux messages —
  // uniquement si l'utilisateur est près du bas et n'a pas remonté lui-même
  useEffect(() => {
    if (isNearBottom && !userScrolledUpRef.current) {
      const isMobile = window.matchMedia(
        "(hover: none) and (pointer: coarse)",
      ).matches;
      messagesEndRef.current?.scrollIntoView({
        behavior: isMobile ? "instant" : "smooth",
      });
    }
  }, [messages.length, pendingMessage, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    const isMobile = window.matchMedia(
      "(hover: none) and (pointer: coarse)",
    ).matches;
    messagesEndRef.current?.scrollIntoView({
      behavior: isMobile ? "instant" : "smooth",
    });
    setIsNearBottom(true);
    userScrolledUpRef.current = false;
  }, []);

  // Met à jour l'état « terminé » dès qu'il est détecté
  useEffect(() => {
    if (isFinished) {
      setIsInterviewFinished(true);
    }
  }, [isFinished]);

  // Trace l'affichage d'une nouvelle question (message de l'assistant)
  const lastMessageCountRef = useRef(-1);
  useEffect(() => {
    if (
      messages.length > 0 &&
      messages[messages.length - 1]?.role === "assistant" &&
      messages.length > lastMessageCountRef.current
    ) {
      questionDisplayedAtRef.current = Date.now();
      typewriterEndAtRef.current = null;
      firstResponseAtRef.current = null;
      onQuestionAffichee();
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, onQuestionAffichee]);

  // Transcription du blob audio
  const handleTranscript = useCallback(
    async (blob: Blob, mimeType: string) => {
      setIsTranscribing(true);
      setIsRecording(false);
      // Note: on ne réinitialise pas audioError ici pour préserver le message de silence détecté
      try {
        // Convertit le blob en base64
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i] as number);
        }
        const audioBase64 = btoa(binary);

        const result = await transcrireAudio({ audioBase64, mimeType });
        if (result.transcript) {
          const transcriptLength = result.transcript.trim().length;
          addToCurrentInput(result.transcript);
          // Tracker le nombre de caractères transcrits
          addTranscribedLength(transcriptLength);
        }
      } catch {
        setAudioError("La transcription a échoué. Veuillez réessayer.");
      } finally {
        setIsTranscribing(false);
      }
    },
    [
      transcrireAudio,
      addToCurrentInput,
      setIsTranscribing,
      setIsRecording,
      addTranscribedLength,
    ],
  );

  const {
    isRecording: isHookRecording,
    isSupported,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    onTranscript: handleTranscript,
    onError: (err) => {
      setAudioError(err);
      setIsRecording(false);
      setRecordingStartTime(null);
    },
    onMaxDurationReached: () => {
      setIsRecording(false);
      setRecordingStartTime(null);
      setAudioError(
        `Limite de ${maxDuration} minutes atteinte. L'enregistrement a été arrêté automatiquement.`,
      );
    },
    onSilenceDetected: () => {
      setIsRecording(false);
      setRecordingStartTime(null);
      setAudioError(
        `Silence détecté pendant ${silenceTimeout} secondes. L'enregistrement a été arrêté automatiquement.`,
      );
    },
    onVolumeChange: (volume) => {
      setAudioVolume(volume);
    },
  });

  // Synchronise l'état d'enregistrement du hook avec le store
  useEffect(() => {
    setIsRecording(isHookRecording);
  }, [isHookRecording, setIsRecording]);

  const handleMicClick = async () => {
    setAudioError(null);
    if (isRecording) {
      stopRecording();
      setRecordingStartTime(null);
    } else {
      const now = Date.now();
      if (firstResponseAtRef.current === null) {
        firstResponseAtRef.current = now;
      }
      setRecordingStartTime(now);
      await startRecording();
    }
  };

  // Envoi d'un message
  const handleSendMessage = async () => {
    if (!currentInput.trim() || isSubmitting) return;

    // Coupe l'enregistrement en cours avant d'envoyer
    if (isRecording) {
      stopRecording();
      return;
    }

    const messageToSend = currentInput.trim();
    const cheatingMeta = getCheatingMeta(messageToSend.length);
    const answeredAt = Date.now();
    const displayedAt = questionDisplayedAtRef.current ?? undefined;
    const typewriterEndAt = typewriterEndAtRef.current ?? undefined;
    const firstResponseAt = firstResponseAtRef.current ?? undefined;

    setCurrentInput("");
    setPendingMessage(messageToSend);
    setIsSubmitting(true);
    setIsTyping(true);

    const result = await envoyerMessage({
      evaluationId: evaluation._id,
      parcoursId: parcoursId as Id<"parcoursEvaluations">,
      token,
      message: messageToSend,
      caracteresTranscrits: transcribedLength,
      cheatingMeta,
      displayedAt,
      answeredAt,
      typewriterEndAt,
      firstResponseAt,
    });

    setPendingMessage(null);
    setIsTyping(false);
    setIsSubmitting(false);
    resetTranscribedLength();
    resetCheatingMeta();
    questionDisplayedAtRef.current = null;
    typewriterEndAtRef.current = null;
    firstResponseAtRef.current = null;

    if (!isBackendError(result)) {
      if (result.estTermine) {
        setIsInterviewFinished(true);
      }
    }
  };

  // Démarrage de l'entretien (depuis la modale d'introduction)
  const handleStartInterview = async () => {
    setIsStartingInterview(true);
    setInitError(null);
    try {
      if (!evaluation.donnees.threadId) {
        const initResult = await initialiserEntretien({
          evaluationId: evaluation._id,
          parcoursId: parcoursId as Id<"parcoursEvaluations">,
          token,
        });
        if ("erreur" in initResult) {
          setInitError(
            "L'initialisation de l'entretien a échoué. Veuillez réessayer.",
          );
          return;
        }
      }
      await demarrerEvaluation({
        evaluationId: evaluation._id,
        parcoursId: parcoursId as Id<"parcoursEvaluations">,
        token,
      });
      setShowIntroModal(false);
    } catch {
      setInitError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsStartingInterview(false);
    }
  };

  // Refus de l'entretien (redirection vers le portail candidat)
  const handleDeclineInterview = () => {
    const baseUrl = candidatePortailUrl ?? "";
    const finalParcoursId = parcoursIdFromStore || parcoursFromEval?.parcoursId;

    // Query encore en chargement et rien dans le store : on attend
    if (parcoursFromEval === undefined && !parcoursIdFromStore) {
      return;
    }

    if (finalParcoursId) {
      window.location.href = `${baseUrl}/p/${finalParcoursId}`;
    } else {
      // Repli sur l'URL de base faute de parcoursId
      window.location.href = baseUrl;
    }
  };

  // Fin de l'entretien
  const handleFinishInterview = async () => {
    setIsSubmitting(true);
    await terminerEvaluation({
      evaluationId: evaluation._id,
      parcoursId: parcoursId as Id<"parcoursEvaluations">,
      token,
      donnees: evaluation.donnees,
    });
    setIsSubmitting(false);
    setShowCompletionModal(true);
  };

  const isMicBusy = isSubmitting || isTranscribing;

  return (
    <>
      <Card className="w-full max-w-4xl flex flex-col shadow-lg h-full sm:h-[85vh] rounded-none sm:rounded-surface py-1">
        {/* Header */}
        <div className="border-b px-4 py-1 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
                FairView
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                {isReadOnly
                  ? "Entretien terminé - Mode lecture seule"
                  : "Répondez aux questions du recruteur"}
              </p>
            </div>
            {!isReadOnly && progression.total > 0 && (
              <div className="flex flex-col items-end gap-1 min-w-24 sm:min-w-40">
                <span className="text-sm md:text-base font-medium">
                  {progression.percentage}%
                </span>
                <Progress
                  value={progression.percentage}
                  className="h-1.5 sm:h-2 w-full"
                />
              </div>
            )}
          </div>
        </div>

        {/* Messages area */}
        <ChatMessages
          messages={messages}
          pendingMessage={pendingMessage}
          isReadOnly={isReadOnly}
          isTyping={isTyping}
          showIntroModal={showIntroModal}
          isNearBottom={isNearBottom}
          isTtsEnabled={isTtsEnabled}
          audioStates={audioStates}
          scrollContainerRef={scrollContainerRef}
          messagesEndRef={messagesEndRef}
          typewriterEndAtRef={typewriterEndAtRef}
          onScroll={handleScroll}
          onScrollToBottom={scrollToBottom}
          onVocalize={handleVocalize}
        />

        {/* Input area */}
        <ChatInputArea
          isReadOnly={isReadOnly}
          isInterviewFinished={isInterviewFinished}
          isSubmitting={isSubmitting}
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          isSupported={isSupported}
          isMobile={isMobile}
          isMicBusy={isMicBusy}
          currentInput={currentInput}
          audioError={audioError}
          audioVolume={audioVolume}
          recordingStartTime={recordingStartTime}
          onInputChange={(newValue, addedChars) => {
            if (addedChars > 0) {
              onKeystroke(addedChars);
              if (firstResponseAtRef.current === null) {
                firstResponseAtRef.current = Date.now();
              }
            }
            setCurrentInput(newValue);
          }}
          onPaste={() => onPaste()}
          onSend={() => void handleSendMessage()}
          onMicClick={() => void handleMicClick()}
          onFinish={() => void handleFinishInterview()}
          isTyping={isTyping}
        />
      </Card>

      {/* Intro Modal */}
      <IntroModal
        isOpen={showIntroModal}
        onConfirm={handleStartInterview}
        onDecline={handleDeclineInterview}
        isLoading={isStartingInterview}
        error={initError}
      />

      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
      />
    </>
  );
}
